import { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import lottie, { type AnimationItem } from "lottie-web";

type Props = {
  mediaType?: string;
  mediaUrl?: string;
  ariaLabel: string;
};

export default function MediaPreviewCell({
  mediaType,
  mediaUrl,
  ariaLabel,
}: Props) {
  const lottieContainerRef = useRef<HTMLDivElement>(null);
  const lottieAnimationRef = useRef<AnimationItem | null>(null);

  useEffect(() => {
    if (mediaType !== "LOTTIE" || !mediaUrl || !lottieContainerRef.current) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(mediaUrl);
        const animationData = (await response.json()) as Record<
          string,
          unknown
        >;

        if (cancelled || !lottieContainerRef.current) return;

        lottieAnimationRef.current?.destroy();
        lottieAnimationRef.current = lottie.loadAnimation({
          container: lottieContainerRef.current,
          renderer: "svg",
          loop: true,
          autoplay: true,
          animationData,
          rendererSettings: { preserveAspectRatio: "xMidYMid meet" },
        });
      } catch {
        console.error("Failed to load Lottie animation:", mediaUrl);
      }
    };

    void load();

    return () => {
      cancelled = true;
      lottieAnimationRef.current?.destroy();
      lottieAnimationRef.current = null;
      if (lottieContainerRef.current) {
        lottieContainerRef.current.innerHTML = "";
      }
    };
  }, [mediaType, mediaUrl]);

  if (!mediaUrl) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <span>-</span>
      </Box>
    );
  }

  if (mediaType === "VIDEO") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <video
          src={mediaUrl}
          muted
          loop
          autoPlay
          playsInline
          style={{ width: 24, height: 43, objectFit: "cover" }}
          aria-label={ariaLabel}
        />
      </Box>
    );
  }

  if (mediaType === "LOTTIE") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <div
          ref={lottieContainerRef}
          style={{ width: 24, height: 43, overflow: "hidden" }}
          aria-label={ariaLabel}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
      <img
        src={mediaUrl}
        alt={ariaLabel}
        style={{ width: 24, height: 43, objectFit: "cover" }}
      />
    </Box>
  );
}
